import { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import privateBadgeStyles from './PrivateBadge.module.css';
import styles from './ShareConfirmDialog.module.css';

interface ShareConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Coordinator-computed z for the `share-confirm` / `detail-share-confirm`
   * layer (A1). Applied inline so it OVERRIDES the shared 9999 backdrop class
   * (the iOS translateZ(0) GPU promotion in that class is preserved); a deeper
   * share screen pushed above confirm then paints over it. Both callers pass
   * it; if absent, the class 9999 is the fallback.
   */
  zIndex?: number;
  /**
   * Whether confirm is the TOPMOST share layer (A1 fix). Under the additive
   * stepped-share model, confirm stays MOUNTED underneath the destination, so
   * its global Escape listener must NOT fire when a deeper share screen (choice
   * / text / mail, and their preview/auth modals) is on top — otherwise Escape
   * would collapse the whole flow instead of letting the top modal close first
   * (the topmost-dismiss contract, README §3.5 nested-modal rule). The owner
   * passes false whenever any deeper share slot is open. Defaults true so a
   * non-stepped caller keeps today's behavior. The backdrop click can't leak
   * (a deeper screen paints over it); only the window Escape needs gating.
   */
  escapeEnabled?: boolean;
}

/**
 * Share-confirmation modal shown before any outbound share path on a
 * private (`isPrivate: true`) prayer. Caller routes through this
 * dialog FIRST, then runs the actual share flow on Confirm.
 *
 * Extracted from the inline portal that previously lived in
 * PrayerDetail.tsx (lines 792-840) per CLAUDE.md rule #4 second-
 * occurrence: PrayerDetail's leaf-sheet image share is one caller;
 * RememberStream's per-moment share affordance is the second.
 *
 * Style split:
 *   - Backdrop + popover chrome via privateBadgeStyles.* — preserves
 *     the iOS Safari z-index + transform:translateZ(0) stacking
 *     workaround documented in CLAUDE.md's PrivateBadge file-map
 *     entry (GPU-promoted siblings can paint above fixed elements
 *     unless the fixed element is also GPU-composited).
 *   - Action row classes (.actions, .cancel, .proceed) owned here
 *     in ShareConfirmDialog.module.css — copied verbatim from the
 *     prior PrayerDetail.module.css definitions.
 *
 * Portals to document.body so the modal escapes any ancestor
 * stacking context (PrayerDetail and RememberStream both sit inside
 * transform-y overlays).
 */
export default function ShareConfirmDialog({
  open,
  onConfirm,
  onCancel,
  zIndex,
  escapeEnabled = true,
}: ShareConfirmDialogProps) {
  const { t } = useTranslation();
  const titleId = `share-confirm-title-${useId()}`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useDialogFocus({
    open,
    active: escapeEnabled,
    containerRef: dialogRef,
    initialFocusRef: cancelRef,
    onEscape: onCancel,
  });

  if (!open) return null;

  return createPortal(
    <div
      className={privateBadgeStyles.backdrop}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        className={privateBadgeStyles.popover}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <h2 id={titleId} className={privateBadgeStyles.popoverTitle}>
          <span className={privateBadgeStyles.popoverStar} aria-hidden="true">✦</span>
          {t('private.shareWarningTitle')}
        </h2>
        <p className={privateBadgeStyles.popoverText}>
          {t('private.shareWarningText')}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            ref={cancelRef}
            className={styles.cancel}
            onClick={onCancel}
          >
            {t('private.shareCancel')}
          </button>
          <button
            type="button"
            className={styles.proceed}
            onClick={onConfirm}
          >
            {t('private.shareConfirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
